import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Tooltip, LinearProgress } from '@mui/material';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import CardMembershipOutlinedIcon from '@mui/icons-material/CardMembershipOutlined';
import type { UsageData } from '../../hooks/useUsage';

interface SidebarProps {
  usage: UsageData | null;
  usageLoading: boolean;
}

const SidebarRoot = styled.aside`
  width: 232px;
  min-width: 232px;
  flex-shrink: 0;
  background: var(--bg);
  border-right: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  height: 100vh;
  position: sticky;
  top: 0;
  padding: 22px 16px;
  box-sizing: border-box;
`;

const BrandRow = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 8px 22px;
  border-bottom: 1px solid var(--border);
  margin-bottom: 14px;
  text-decoration: none;
  color: inherit;
`;

const BrandMark = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: linear-gradient(135deg, #00e5c3, #00d4ff);
  display: grid;
  place-items: center;
  box-shadow: 0 4px 16px rgba(0, 212, 255, 0.25);

  svg {
    width: 18px;
    height: 18px;
  }
`;

const BrandName = styled.span`
  font-family: 'Syne', sans-serif;
  font-weight: 800;
  font-size: 16px;
  letter-spacing: 0.5px;
  color: var(--t1);
`;

const NavSectionLabel = styled.div`
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: var(--t4);
  padding: 14px 8px 8px;
`;

const NavItem = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: var(--t2);
  text-decoration: none;
  cursor: pointer;
  transition: background 120ms, color 120ms;

  &:hover {
    background: var(--s2);
    color: var(--t1);
  }

  &.active {
    background: var(--teal-dim);
    color: var(--t1);
    font-weight: 600;
  }

  &.active .nav-dot {
    background: var(--teal);
    box-shadow: 0 0 0 3px var(--teal-glow);
  }
`;

const NavDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--t4);
  flex-shrink: 0;
  transition: background 120ms, box-shadow 120ms;
`;

const NavIcon = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: auto;
  color: var(--t4);
  font-size: 16px;
`;

const FooterCard = styled.div`
  margin-top: auto;
  padding: 12px;
  border-radius: 10px;
  background: var(--s2);
  font-size: 12px;
`;

const CreditRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 11px;
  color: var(--t3);
  font-weight: 500;
`;

const CreditNum = styled.span`
  color: var(--t1);
  font-weight: 600;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
`;

const TopUpLink = styled.button`
  background: none;
  border: none;
  color: var(--teal);
  font-weight: 600;
  font-size: 11px;
  cursor: pointer;
  padding: 0;
  margin-top: 10px;
`;

function getBarColor(percent: number): 'success' | 'warning' | 'error' {
  if (percent >= 90) return 'error';
  if (percent >= 70) return 'warning';
  return 'success';
}

const Sidebar: React.FC<SidebarProps> = ({ usage, usageLoading }) => {
  const navigate = useNavigate();
  const showUsageBar = !usageLoading && usage !== null && usage.limit !== null;
  const isExceeded =
    usage !== null && usage.limit !== null && usage.used >= usage.limit;

  return (
    <SidebarRoot>
      <BrandRow to="/matters" end>
        <BrandMark>
          <svg viewBox="0 0 64 64" fill="none" aria-hidden>
            <defs>
              <linearGradient id="sidebar-logo-g" x1="0" y1="64" x2="64" y2="0">
                <stop stopColor="#ffffff" stopOpacity="0.9" />
                <stop offset="1" stopColor="#ffffff" />
              </linearGradient>
            </defs>
            <path
              d="M12 14 H40 C54 14 54 32 42 32 Q39 22 36 32 Q33 42 30 32 Q26 22 22 32 C10 32 10 50 24 50 H52"
              stroke="url(#sidebar-logo-g)"
              strokeWidth="7"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </BrandMark>
        <BrandName>RAF</BrandName>
      </BrandRow>

      <NavSectionLabel>Workspace</NavSectionLabel>
      <NavItem to="/matters">
        <NavDot className="nav-dot" />
        Matters
        <NavIcon>
          <FolderOpenIcon fontSize="inherit" />
        </NavIcon>
      </NavItem>

      <NavSectionLabel>Account</NavSectionLabel>
      <NavItem to="/subscription">
        <NavDot className="nav-dot" />
        Subscription
        <NavIcon>
          <CardMembershipOutlinedIcon fontSize="inherit" />
        </NavIcon>
      </NavItem>
      <NavItem to="/settings">
        <NavDot className="nav-dot" />
        Settings
        <NavIcon>
          <SettingsOutlinedIcon fontSize="inherit" />
        </NavIcon>
      </NavItem>

      <FooterCard>
        {showUsageBar && usage && usage.limit !== null ? (
          <Tooltip
            title={`${usage.totalJobs} jobs this month · $${usage.used.toFixed(2)} of $${usage.limit.toFixed(2)} AI budget used`}
            arrow
            placement="top"
          >
            <div onClick={() => navigate('/subscription')} style={{ cursor: 'pointer' }}>
              <CreditRow>
                <span>Credit balance</span>
                <CreditNum>
                  ${usage.used.toFixed(2)} / ${usage.limit.toFixed(2)}
                </CreditNum>
              </CreditRow>
              <LinearProgress
                variant="determinate"
                value={Math.min(usage.percentUsed, 100)}
                color={getBarColor(usage.percentUsed)}
                sx={{ height: 4, borderRadius: 100, backgroundColor: 'var(--s3)' }}
              />
              {isExceeded ? (
                <TopUpLink onClick={(e) => { e.stopPropagation(); navigate('/pricing'); }}>
                  Upgrade →
                </TopUpLink>
              ) : (
                <TopUpLink onClick={(e) => { e.stopPropagation(); navigate('/subscription'); }}>
                  Top up →
                </TopUpLink>
              )}
            </div>
          </Tooltip>
        ) : (
          <CreditRow>
            <span>Credit balance</span>
            <CreditNum>—</CreditNum>
          </CreditRow>
        )}
      </FooterCard>
    </SidebarRoot>
  );
};

export default Sidebar;
